import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Notice } from "@/components/ui/login-card";
import { ResourcePageLayout } from "@/components/resource-page";
import { BusyButton } from "@/components/button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useAcceptPendingInvite, useCreateWorkspace, usePendingInvites } from "@/lib/session";

/**
 * An account in no workspace at all.
 *
 * This file used to hold the list of workspaces, which was a screen and a menu
 * row of its own. The fleet has neither — you are always inside a workspace and
 * the switch is how you leave one — so `/` sends somebody on instead, and what
 * is left here is the one case it cannot send anywhere.
 *
 * The state used to be reachable only by `listWorkspaces` hiding a soft-deleted
 * workspace — the personal one came from Better Auth's `user.create` hook,
 * unconditionally, so there was nothing this screen could offer beyond saying
 * what it sees. That stopped being true 2026-09-25: registration now skips
 * `ensurePersonalWorkspace` for an e-mail with an open invite waiting
 * (`sitebrewhq/sitebrew-api#252`, reversing the 2026-09-23 decision), on
 * purpose, so that person does not end up owning a second, unwanted workspace
 * alongside the one they were invited to. That reopens exactly the gap this
 * screen used to have nothing for — an account that finishes registering
 * before it finishes accepting is briefly a member of nothing — so this now
 * offers the two ways out Jakub asked for: accept one of the account's own
 * open invites (`/v1/invites/mine`, no mail link required), or, if there is
 * none, start a personal workspace the ordinary way (`POST /v1/workspaces`,
 * the same call `CreateWorkspaceModal` makes from the switcher).
 */
export function NoWorkspaceScreen() {
  const navigate = useNavigate();
  const invites = usePendingInvites();
  const acceptInvite = useAcceptPendingInvite();
  const createWorkspace = useCreateWorkspace();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [name, setName] = useState("");

  const goTo = (workspaceId: string) =>
    void navigate({ to: "/workspaces/$workspaceId/sites", params: { workspaceId } });

  const handleAccept = (inviteId: string) => {
    setAcceptingId(inviteId);
    acceptInvite.mutate(inviteId, {
      onSuccess: (result) => goTo(result.workspaceId),
      onSettled: () => setAcceptingId(null),
    });
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createWorkspace.mutate({ name: trimmed }, { onSuccess: (workspace) => goTo(workspace.id) });
  };

  const acceptFailure =
    acceptInvite.error instanceof ApiError
      ? acceptInvite.error.message
      : acceptInvite.error
        ? "Failed to accept invite"
        : null;
  const createFailure =
    createWorkspace.error instanceof ApiError
      ? createWorkspace.error.message
      : createWorkspace.error
        ? "Failed to create workspace"
        : null;

  if (invites.isLoading) {
    return (
      <ResourcePageLayout title="Workspaces">
        <Notice>Loading…</Notice>
      </ResourcePageLayout>
    );
  }

  const pending = invites.data ?? [];

  return (
    <ResourcePageLayout title="Workspaces">
      {pending.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You have been invited to join {pending.length === 1 ? "a workspace" : "these workspaces"}.
          </p>
          {acceptFailure ? <Notice>{acceptFailure}</Notice> : null}
          <div className="space-y-2">
            {pending.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div>
                  <div className="font-medium">{invite.workspaceName}</div>
                  <div className="text-sm text-muted-foreground">as {invite.role}</div>
                </div>
                <BusyButton
                  busy={acceptingId === invite.id && acceptInvite.isPending}
                  disabled={acceptInvite.isPending}
                  onClick={() => handleAccept(invite.id)}
                >
                  Accept
                </BusyButton>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="max-w-sm space-y-4">
          <p className="text-sm text-muted-foreground">
            You are not a member of any workspace yet. Start your own.
          </p>
          <div className="space-y-2">
            <Label htmlFor="new-workspace-name">Workspace name</Label>
            <Input
              id="new-workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Client project"
              autoFocus
            />
          </div>
          {createFailure ? <Notice>{createFailure}</Notice> : null}
          <Button
            type="button"
            onClick={handleCreate}
            disabled={createWorkspace.isPending || !name.trim()}
          >
            {createWorkspace.isPending ? "Creating…" : "Create workspace"}
          </Button>
        </div>
      )}
    </ResourcePageLayout>
  );
}
